'use client';

import React, { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { KalshiNewsScrollBanner } from '../components/KalshiNewsScrollBanner';
import { pinEmbedPalette } from '../../lib/embedPalette';
import { embedPaletteFromParams, resolveTickerSpeed } from '../../lib/embedParams';
import KalshiLogo from '@components/DesignSystem/Icons/adhoc/KalshiLogo';

// The ticker strip embeds at a fixed 60px. Reserve that height across every state
// (loading / empty / error / the banner's own logo-first render) so it never snaps
// from logo-height to full-height when the tickers arrive -- no layout shift on load.
const STRIP_HEIGHT = 60;
const MAX_BANNER_TICKERS = 12;

const normalizeTickers = (tickers: string[]) =>
    [...new Set(tickers.map((ticker) => ticker.trim()).filter(Boolean))].slice(0, MAX_BANNER_TICKERS);

const messageStyle: React.CSSProperties = {
    width: '100%',
    textAlign: 'center',
    color: '#666',
    fontSize: '14px',
};

/** decodeURIComponent throws on a malformed `%` escape, which blanked the whole strip. */
function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function BannerContent() {
    const searchParams = useSearchParams();

    const tickers = searchParams.get('tickers') || '';
    const category = searchParams.get('category') || '';
    const logoColor = safeDecode(searchParams.get('logocolor') || '#09bc7f');
    // One resolved value drives both colour layers: the JS Palette surface below and the
    // CSS variables pinned in the effect. Reading them from separate expressions is how the
    // strip ended up with a light surface and dark-mode text variables.
    const embedPalette = embedPaletteFromParams(searchParams);
    // Partner embeds (news.kalshi.com among them) already pass `speed`; it used to be
    // parsed by nothing and silently dropped.
    const speed = resolveTickerSpeed(searchParams.get('speed'));
    const showLogoParam = searchParams.get('showLogo');
    const showLogo = showLogoParam !== 'false' && showLogoParam !== '0';
    const utmSource = searchParams.get('utm_source') || undefined;
    const utmMedium = 'widget';
    const utmCampaign = searchParams.get('utm_campaign') || undefined;
    const referral = searchParams.get('referral') || undefined;
    const [tickerArray, setTickerArray] = React.useState<string[]>([]);
    const [bannerError, setBannerError] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    // If category is provided, fetch tickers for that category, refresh every 60s
    React.useEffect(() => {
        if (!category) {
            setTickerArray(normalizeTickers(tickers.split(',')));
            return;
        }

        let intervalId: ReturnType<typeof setInterval>;

        const fetchCategoryTickers = (isInitial = false) => {
            if (isInitial) setLoading(true);
            fetch(`/api/widgets/tickers-by-category?category=${encodeURIComponent(category)}`)
                .then((res) => res.json())
                .then((data) => {
                    const categoryMarkets = data.categories?.[category] || [];
                    const next = normalizeTickers(categoryMarkets.map((m: any) => m.event_ticker));
                    setTickerArray((prev) => {
                        if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
                        return next;
                    });
                })
                .catch((error) => {
                    console.warn('Error fetching category tickers:', error);
                    if (isInitial) setBannerError(true);
                })
                .finally(() => {
                    if (isInitial) setLoading(false);
                });
        };

        fetchCategoryTickers(true);
        intervalId = setInterval(() => fetchCategoryTickers(false), 60_000);
        return () => clearInterval(intervalId);
    }, [category, tickers]);

    // The strip paints its own surface from the JS Palette (via `isDark`) but colors its text
    // with CSS-variable classes (`text-fill-x10`), so both layers have to agree. Pin the
    // variables to the theme this embed asked for; without it the colorPalette bridge resolves
    // the visitor's own theme once their session restores and the two layers diverge, leaving a
    // light ticker with white text on its white strip.
    React.useEffect(() => {
        pinEmbedPalette(embedPalette);
    }, [embedPalette]);

    React.useEffect(() => {
        // Ensure body has no margins and proper styles for iframe embedding
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.backgroundColor = 'transparent';
        document.body.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    }, []);

    const renderBanner = () => {
        if (loading) {
            return <div style={messageStyle}>Loading…</div>;
        }

        if (tickerArray.length === 0) {
            return <div style={messageStyle}>No tickers provided</div>;
        }

        if (!bannerError) {
            return (
                <div onError={() => setBannerError(true)} style={{ width: '100%' }}>
                    <KalshiNewsScrollBanner
                        EVENT_TICKERS={tickerArray}
                        maxItems={MAX_BANNER_TICKERS}
                        speed={speed}
                        isDark={embedPalette === 'dark'}
                        showBranding={showLogo}
                        logoColor={logoColor}
                        utmSource={utmSource}
                        utmMedium={utmMedium}
                        utmCampaign={utmCampaign}
                        referral={referral}
                    />
                </div>
            );
        }

        // Fallback banner if the original component fails
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                {showLogo && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <KalshiLogo color={logoColor} />
                    </div>
                )}
                <div style={{ ...messageStyle, flex: 1 }}>Loading ticker data…</div>
            </div>
        );
    };

    return (
        <div
            style={{
                minHeight: STRIP_HEIGHT,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
            }}
        >
            {renderBanner()}
        </div>
    );
}

export default function BannerPage() {
    // Blank reserved strip (no "Loading…" flash) until the client component mounts.
    return (
        <Suspense fallback={<div style={{ minHeight: STRIP_HEIGHT, width: '100%' }} />}>
            <BannerContent />
        </Suspense>
    );
}
